import { useEffect, useRef, useState } from "react";
import { duration } from "utils/fieldFormat";
import { More } from "./radarUi";
import {
  RICH_LINES,
  estimateRichData,
  megabytes,
  milliampHours,
  richDataLines,
} from "utils/radarAnalysis";

// Before you apply (Node Control): what radar rich data costs, beside the
// node's other estimates. The projection's own lines (`radar`: one per kind
// switched on, already in its totals: battery mAh and data MB a day and a
// month, with their sources - rich readings on every wake, bursts, streaming)
// when the server gives them; otherwise, from a server before them, our
// estimate of the data from the message sizes the firmware sends, without the
// battery; and our estimate of rich readings beside a server's lines that
// leave them out (not in its totals). `plan` is the Radar rich data card's
// ({rich, stream, burst, raw, planned}); `uploadSeconds` the scenario's upload
// cadence (for our own estimate: bursts come on every Nth upload) and
// `wakeSeconds` its wake cadence (rich readings come on every wake);
// `liveHours` / `onLiveHours` the hours a day of live mode the projection
// assumes for streaming (null: the node's own).
const CAPTURES = {
  alternating: "spectrum and beat signal in turn",
  beat_signal: "with the beat signal",
  spectrum: "spectrum only",
  echo_list: "echo list only",
  both: "beat signal and spectrum",
  beat_signal_and_spectrum: "beat signal and spectrum",
};
// What a rich reading's capture holds, a part each.
const RICH_CAPTURES = {
  beat_signal: "beat signal",
  spectrum: "spectrum",
  both: "beat signal and spectrum",
  beat_signal_and_spectrum: "beat signal and spectrum",
};
const number = (v, digits = 1) =>
  v.toLocaleString(undefined, { maximumFractionDigits: digits });
const bytes = (v) =>
  v >= 1000 ? `${number(v / 1000, 1)} kB` : `${Math.round(v)} B`;

// What the rich line's battery rests on: mAh a capture (mah_capture), so
// a normal wake's is that times its captures; measured on the node's own
// rich readings, else the backend's 0.3 mAh a capture until it has three.
// Beside the captures, the backend's .59 model counts the energy meter's
// hourly baseline (rich readings) and, over LTE, each capture message's send,
// and its .62 support the node's own estimate of each beat signal:
// "; plus 2.64 mAh a day for the energy meter's hourly baseline, 14.4 mAh a
// day to send the captures over cellular and 0.22 mAh a day for the node's
// own estimate".
function besides(line) {
  const parts = [
    line.baselineMahDay
      ? `${number(line.baselineMahDay, 2)} mAh a day for the energy meter's hourly baseline`
      : null,
    line.sendMahDay
      ? `${number(line.sendMahDay, 2)} mAh a day to send the captures over cellular`
      : null,
    line.edgeMahDay
      ? `${number(line.edgeMahDay, 3)} mAh a day for the on-node estimate`
      : null,
  ].filter(Boolean);
  return parts.length
    ? `; plus ${parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0]}`
    : "";
}
// Firmware .62's hybrid upload on the rich line: the capture messages sent
// of the captures taken, and the share of rich wakes whose raw captures go
// up (1 in N, modelled; else measured on the node's own notes).
function keptText(line) {
  if (!line.keep || line.uploadsDay === null) return null;
  const share =
    line.keep.shareSource === "measured"
      ? `${number(line.keep.share * 100)} % of rich wakes, measured on the node's last ${number(line.keep.readings ?? 0, 0)}`
      : line.keep.every > 1
        ? `1 rich wake in ${number(line.keep.every, 0)}, modelled`
        : `${Math.round(line.keep.share * 100)} % of rich wakes, modelled`;
  return `raw captures sent: ${number(line.uploadsDay)}${line.capturesDay !== null ? ` of ${number(line.capturesDay)}` : ""} a day (${share})`;
}
// The node's own estimate in a line's data: "the node's own estimate: 36 kB
// a day".
const edgeData = (line) =>
  line.edgeBytesDay
    ? `the on-node estimate: ${bytes(line.edgeBytesDay)} a day`
    : null;

// Firmware .60: the radar's rate a line rests on, when it is not the factory
// 9,600 or was chosen here: "115,200 baud, as set in Radar link".
const BAUD_SOURCES = {
  posted: "as set in Radar link",
  read_back: "the rate the radar answers at",
  default: "the radar's factory rate",
};
function rateText(line) {
  if (line.baud === null || line.baud === undefined) return null;
  if (line.baud === 9600 && line.baudSource !== "posted") return null;
  const source = BAUD_SOURCES[line.baudSource];
  return `${line.baud.toLocaleString()} baud${source ? `, ${source}` : ""}`;
}

function richBattery(line) {
  const wake =
    line.mahCapture !== null && line.captures
      ? line.mahCapture * line.captures
      : null;
  const each = [
    line.mahCapture !== null
      ? `${number(line.mahCapture, 3)} mAh a capture`
      : null,
    wake !== null ? `${number(wake, 3)} mAh a wake` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const lead = each ? `${each}: ` : "";
  const how =
    line.batterySource === "measured"
      ? `${lead}the median of ${line.measuredBursts ?? "its"} measured rich reading${line.measuredBursts === 1 ? "" : "s"}, per capture`
      : line.batterySource === "model"
        ? `${lead}${line.captureS !== null && line.captureS !== undefined ? `modelled at ${number(line.captureS, 2)} s a capture` : "assumed"}${rateText(line) ? ` (${rateText(line)})` : ""} until the node has measured three rich readings`
        : each || "";
  return `${how}${besides(line)}` || null;
}

function batteryCell(line, key) {
  if (line.off) return ["—", null];
  if (line.batterySource === "external_power" || key === "stream")
    return ["None: live mode runs on external power", null];
  // Rich readings cost on every wake, bursts on the wakes that upload.
  const unit = key === "rich" ? "wake" : "burst";
  const main = line.mahDay
    ? `≈ ${milliampHours(line.mahDay)} a day`
    : line.source === "estimate" && line.radarSeconds
      ? `Not measured: the radar runs ≈ ${duration(line.radarSeconds)} a ${unit}, ${duration(line.radarDay)} a day`
      : "Not estimated";
  if (line.source !== "server") return [main, null];
  if (key === "rich") return [main, richBattery(line)];
  const each =
    line.mahEach !== null ? `${number(line.mahEach, 3)} mAh a ${unit}` : null;
  const how =
    line.batterySource === "measured"
      ? `${each ? `${each}: ` : ""}the median of ${line.measuredBursts ?? "its"} measured ${unit}${line.measuredBursts === 1 ? "" : "s"}${key === "rich" ? "" : ", scaled to this burst's length"}`
      : line.batterySource === "model"
        ? `${each ? `${each}: ` : ""}${line.secondsEach !== null ? `${number(line.secondsEach)} s of radar` : "the radar's time"}${rateText(line) ? ` (${rateText(line)})` : ""} at ${line.currentMa !== null ? `${number(line.currentMa)} mA` : "the active current"} (${line.currentSource === "readings" ? "the node's median current" : "the assumed active current"})`
        : each;
  return [
    main,
    how ? `${how}${besides(line)}` : besides(line).slice(2) || null,
  ];
}

// What the rich line's data rests on: wakes and the captures each takes (an
// upload wake's, all of them, when it takes more), captures a day, and the
// battery floor, under which none are taken (not modelled).
const richKind = (capture) => RICH_CAPTURES[capture] || capture;
const s = (n) => (n === 1 ? "" : "s");
function richData(line) {
  const upload =
    line.capturesUpload !== null && line.capturesUpload > (line.captures ?? 0);
  return [
    line.captures
      ? `${number(line.wakesDay ?? line.perDay ?? 0)} wakes a day · ${line.captures} capture${s(line.captures)} a wake${line.capture ? ` (${richKind(line.capture)})` : ""}`
      : line.perDay !== null
        ? `${number(line.perDay)} readings a day, on upload wakes only`
        : null,
    upload
      ? `${line.capturesUpload} on ${line.captures ? "each upload wake" : "each"}${line.captures && line.uploadWakesDay !== null ? ` (${number(line.uploadWakesDay)} a day)` : ""}${line.captureUpload ? `: ${richKind(line.captureUpload)}` : ""}`
      : null,
    line.capturesDay !== null
      ? `${number(line.capturesDay)} capture${s(line.capturesDay)} a day`
      : null,
    keptText(line),
    edgeData(line),
    line.minBv !== null
      ? `none taken below ${line.minBv.toLocaleString()} mV (not modelled)`
      : null,
  ];
}

function dataCell(line, key, plannedStreamS = null) {
  if (line.off) return ["Off", null];
  if (key === "stream" && line.source === "estimate" && line.mbHour)
    return [
      `≈ ${megabytes({ low: line.mbHour, high: line.mbHour })} an hour of live mode (${megabytes(line.mbDay)} a whole day)`,
      null,
    ];
  const day = megabytes(line.mbDay);
  const month = megabytes(line.mbMonth);
  const main =
    key === "stream" && line.mbLiveHour !== null
      ? `≈ ${megabytes({ low: line.mbLiveHour, high: line.mbLiveHour })} an hour of live mode${line.liveHoursDay !== null ? ` · ${number(line.liveHoursDay)} h a day: ${day || "nothing"}${month ? `, ${month} a month` : ""}` : ""}`
      : day || month
        ? `≈ ${[day && `${day} a day`, month && `${month} a month`].filter(Boolean).join(" · ")}`
        : "Not estimated";
  if (line.source !== "server") return [main, line.note];
  const kind = CAPTURES[line.capture] || line.capture;
  const how =
    key === "rich"
      ? richData(line)
      : key === "burst"
        ? [
            line.perDay !== null
              ? `${number(line.perDay)} burst${line.perDay === 1 ? "" : "s"} a day`
              : null,
            line.captures !== null
              ? `${line.captures} capture${line.captures === 1 ? "" : "s"}${kind ? ` (${kind})` : ""}`
              : null,
            line.bytesEach !== null ? `${bytes(line.bytesEach)} a burst` : null,
            line.daylightSource
              ? `only with enough sun: ${Math.round((line.daylightShare ?? 0) * 100)} % of upload wakes (${line.daylightSource === "readings" ? "the node's last 7 days" : "assumed half"})`
              : null,
            edgeData(line),
          ]
        : [
            // Firmware .60: the interval used, at least 10 s below 38,400
            // baud.
            line.streamS !== null && line.streamS !== undefined
              ? `a capture every ${number(line.streamS)} s${Number.isFinite(plannedStreamS) && line.streamS > plannedStreamS ? ` (asked every ${number(plannedStreamS)} s: at least 10 s apart below 38,400 baud)` : ""}`
              : null,
            line.perLiveHour !== null
              ? `${number(line.perLiveHour)} capture${line.perLiveHour === 1 ? "" : "s"} an hour of live mode${kind ? ` (${kind})` : ""}`
              : null,
            line.bytesEach !== null ? `${bytes(line.bytesEach)} each` : null,
          ];
  return [main, [...how, line.note].filter(Boolean).join(" · ") || null];
}

const SOURCE_WORDS = {
  measured: "Battery measured on this node",
  model: "Battery modelled",
  external_power: "External power",
};
function sourceCell(line) {
  if (line.off) return "—";
  if (line.source === "estimate") return "Estimated here";
  return (
    [
      SOURCE_WORDS[line.batterySource] ||
        (line.batterySource && `Battery: ${line.batterySource}`),
      line.dataSource &&
        `data ${line.dataSource === "model" ? "modelled" : line.dataSource}`,
    ]
      .filter(Boolean)
      .join(" · ") || "From the server"
  );
}

// Hours a day of live mode for the stream's line: blank is the node's own.
function LiveHours({ value, onChange }) {
  const [text, setText] = useState(value === null ? "" : String(value));
  useEffect(() => {
    setText((old) =>
      value === null
        ? old.trim() === ""
          ? old
          : ""
        : Number(old) === value
          ? old
          : String(value),
    );
  }, [value]);
  const bad =
    text.trim() !== "" &&
    !(Number.isFinite(Number(text)) && Number(text) >= 0 && Number(text) <= 24);
  return (
    <label className="field-rich-hours" htmlFor="field-rich-live-hours">
      Live hours a day, for streaming
      <input
        id="field-rich-live-hours"
        type="number"
        inputMode="decimal"
        min={0}
        max={24}
        step="any"
        placeholder="the node's own"
        value={text}
        aria-invalid={bad || undefined}
        onChange={(e) => {
          const t = e.target.value;
          setText(t);
          if (t.trim() === "") onChange?.(null);
          else if (Number(t) >= 0 && Number(t) <= 24) onChange?.(Number(t));
        }}
      />
      <small>
        {bad
          ? "From 0 to 24, or blank."
          : "Blank: the node's own - 24 while its live command keeps it live, else what is left of its window."}
      </small>
    </label>
  );
}

export default function RichDataCosts({
  projection,
  plan,
  uploadSeconds = null,
  wakeSeconds = null,
  cellular = false,
  failed = false,
  liveHours = null,
  onLiveHours,
}) {
  const answered = richDataLines(projection);
  // While the projection is worked out again, its last lines stay (marked
  // as recalculating), so the table and the live-hours field do not jump;
  // with none yet, no stand-in that the answer would replace.
  const waiting = !projection && !failed;
  const kept = useRef(null);
  if (answered) kept.current = answered;
  else if (!waiting) kept.current = null;
  const server = answered ?? (waiting ? kept.current : null);
  const recalculating = waiting && !!server;
  // The projection's warnings about the radar's rate (firmware .60: a
  // capture modelled at a faster rate than 9,600 baud), beside the lines they
  // concern; kept while it is worked out again, like the lines.
  const keptWarnings = useRef([]);
  if (projection)
    keptWarnings.current = (
      Array.isArray(projection.warnings) ? projection.warnings : []
    ).filter((w) => typeof w === "string" && /\bbaud\b/.test(w));
  else if (!waiting) keptWarnings.current = [];
  const baudWarnings = server ? keptWarnings.current : [];
  const planned = {
    ...plan?.rich,
    ...plan?.stream,
    ...plan?.burst,
    ...plan?.raw,
  };
  const ours = waiting
    ? []
    : estimateRichData(planned, { uploadSeconds, wakeSeconds });
  // A server whose projection leaves rich readings out (one before them):
  // ours beside its lines, not in its totals.
  const richOurs =
    server && !server.some((l) => l.key === "rich")
      ? ours.find((l) => l.key === "rich" && !l.off)
      : null;
  const lines = server
    ? richOurs
      ? [
          ...server,
          {
            ...richOurs,
            note: "Estimated here: this server's projection has no line for rich readings yet, so they are not in the totals above",
          },
        ]
      : server
    : ours;
  const known = RICH_LINES.map(([key, label]) => [
    key,
    label,
    lines.find((l) => l.key === key),
  ]).filter(([, , line]) => line);
  const extra = (server || [])
    .filter((l) => !RICH_LINES.some(([key]) => key === l.key))
    .map((l) => [l.key, l.label || l.key.replaceAll("_", " "), l]);
  const rows = [...known, ...extra];
  const streaming =
    rows.some(([key, , line]) => key === "stream" && !line.off) ||
    (plan?.stream?.radar_stream ?? 0) > 0;
  const allOff = rows.every(([, , line]) => line.off);
  const off = !!server && !server.length && !richOurs;
  const richPlan = !!plan?.rich;
  if (!rows.length && !off && !waiting) return null;
  return (
    <div
      className="field-rich-costs"
      role="group"
      aria-labelledby="field-rich-costs"
    >
      <h3 id="field-rich-costs" className="field-radar-heading">
        Radar rich data
      </h3>
      {waiting && !server ? (
        <p className="field-caption">Calculating…</p>
      ) : off ? (
        <p className="field-caption">
          {richPlan ? "Rich readings, bursts" : "Bursts"} and streaming are off
          at these settings: nothing extra in the estimates above.
        </p>
      ) : (
        <>
          <div
            className={`field-stream-table${recalculating ? " is-stale" : ""}`}
            aria-busy={recalculating || undefined}
          >
            <table className="field-table" aria-label="Radar rich data costs">
              <thead>
                <tr>
                  <th scope="col">What</th>
                  <th scope="col">Battery</th>
                  <th scope="col">Data</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([key, label, line]) => {
                  const [battery, batteryHow] = batteryCell(line, key);
                  const [data, dataHow] = dataCell(
                    line,
                    key,
                    planned.radar_stream_s,
                  );
                  return (
                    <tr key={key}>
                      <th scope="row">
                        {line.label || label}
                        <small>
                          {line.off
                            ? "off"
                            : plan?.planned?.[key] ||
                                (plan?.planned?.baud &&
                                  (key === "rich" || key === "burst")) ||
                                // The node's own estimate, where the line
                                // models it (the backend's .62 support).
                                (plan?.planned?.edge &&
                                  line.edge !== null &&
                                  line.edge !== undefined)
                              ? "as set in Radar rich data, not applied yet"
                              : "as the node runs now"}
                        </small>
                      </th>
                      <td>
                        {battery}
                        {batteryHow && <small>{batteryHow}</small>}
                      </td>
                      <td>
                        {data}
                        {dataHow && <small>{dataHow}</small>}
                      </td>
                      <td>{sourceCell(line)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {baudWarnings.map((w) => (
            <p className="field-radar-caution" role="note" key={w}>
              {w}
            </p>
          ))}
          <p className="field-caption">
            {recalculating ? "Recalculating for the new settings… " : ""}
            {allOff
              ? "Rich data is off: nothing extra. "
              : server
                ? "From the projection at these settings, already included in the totals above. "
                : `This server does not estimate rich data yet, so the data is estimated here from the message sizes the firmware sends${uploadSeconds ? `, with a burst on every Nth upload at one upload every ${duration(uploadSeconds)}` : ""}${rows.some(([key, , line]) => key === "rich" && !line.off) && wakeSeconds ? `${uploadSeconds ? " and" : ", with"} rich readings on every wake, one every ${duration(wakeSeconds)}` : ""}; the battery is not estimated (Radar analysis shows each burst's measured energy from firmware rtct.57). `}
            Streaming runs only in live mode, on external power.
            {cellular ? " On cellular, both go over the SIM." : ""}
          </p>
          {server && !allOff && (
            <More label="Where the figures come from">
              <p className="field-caption">
                The battery is measured on this node where it has three measured
                bursts or more (firmware rtct.57 measures each one's energy),
                modelled otherwise; the data comes from the message sizes the
                firmware sends.
              </p>
            </More>
          )}
        </>
      )}
      {server && streaming && (
        <LiveHours value={liveHours} onChange={onLiveHours} />
      )}
    </div>
  );
}
